import { useState, useEffect } from 'react';
import { Sparkles, ArrowRightLeft, Calendar, Clock, Globe } from 'lucide-react';
import { Switch } from '@/components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useAuthStore } from '@/stores/authStore';
import { useOnboardingDataStore } from '@/stores/onboardingDataStore';
import { upsertPlanningSetup, upsertPlanningPreferences, updateUserTimezone } from '@/services/data/planningPreferences';
import { cn } from '@/lib/utils';
import { ManualPlanningConfig } from '@/components/planning/ManualPlanningConfig';
import { TimezoneSelect } from '@/components/ui/timezone-select';
import type { ReschedulingStrategy } from '@/types/scheduling';

interface EditorialPlanningStepProps {
  readonly onComplete: () => void;
  readonly setLoading: (loading: boolean) => void;
}

const RESCHEDULING_OPTIONS: Array<{
  value: ReschedulingStrategy;
  label: string;
  description: string;
  icon: typeof ArrowRightLeft;
}> = [
  {
    value: 'shift-all',
    label: 'Décaler tout',
    description: 'Décale toutes les publications suivantes',
    icon: ArrowRightLeft
  },
  {
    value: 'next-slot-strict',
    label: 'Prochain créneau',
    description: 'Publie au prochain créneau disponible',
    icon: Clock
  },
  {
    value: 'next-day-flexible',
    label: 'Jour suivant',
    description: 'Publie le jour suivant de manière flexible',
    icon: Calendar
  }
];

// Get browser timezone or default to Europe/Paris
function getBrowserTimezone(): string {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || 'Europe/Paris';
  } catch {
    return 'Europe/Paris';
  }
}

export function EditorialPlanningStep({
  onComplete,
  setLoading
}: EditorialPlanningStepProps) {
  const { userId } = useAuthStore();
  const planningPreferences = useOnboardingDataStore((state) => state.planningPreferences);
  const updatePlanningPreferences = useOnboardingDataStore((state) => state.updatePlanningPreferences);
  
  // Form state - initialize from preloaded data
  const [isAutoPlanning, setIsAutoPlanning] = useState(
    (planningPreferences as any)?.is_auto_planning ?? true
  );
  const [reschedulingStrategy, setReschedulingStrategy] = useState<ReschedulingStrategy>(
    planningPreferences?.rescheduling_strategy || 'shift-all'
  );
  const [timezone, setTimezone] = useState<string>(
    (planningPreferences as any)?.timezone || getBrowserTimezone()
  );

  // Sync with preloaded data when it becomes available
  useEffect(() => {
    if (planningPreferences) {
      if ((planningPreferences as any).is_auto_planning !== undefined) {
        setIsAutoPlanning((planningPreferences as any).is_auto_planning);
      }
      if (planningPreferences.rescheduling_strategy) {
        setReschedulingStrategy(planningPreferences.rescheduling_strategy);
      }
      if ((planningPreferences as any).timezone) {
        setTimezone((planningPreferences as any).timezone);
      }
    }
  }, [planningPreferences]);

  const handleToggle = async (enabled: boolean) => {
    if (!userId) return;
    setIsAutoPlanning(enabled);
    setLoading(true);

    // Optimistic update in store
    updatePlanningPreferences({ is_auto_planning: enabled } as any);
    
    // Save the preference
    await upsertPlanningSetup(userId, 10, {}, enabled);
    setLoading(false);
  };

  const handleStrategyChange = async (strategy: ReschedulingStrategy) => {
    if (!userId) return;
    setReschedulingStrategy(strategy);
    setLoading(true);

    // Optimistic update in store
    updatePlanningPreferences({ rescheduling_strategy: strategy });
    
    await upsertPlanningPreferences(userId, strategy);
    setLoading(false);
  };

  const handleTimezoneChange = async (newTimezone: string) => {
    if (!userId) return;
    setTimezone(newTimezone);
    setLoading(true);

    // Optimistic update in store
    updatePlanningPreferences({ timezone: newTimezone } as any);
    
    await updateUserTimezone(userId, newTimezone);
    setLoading(false);
  };

  return (
    <div className="space-y-6">
      {/* Timezone selection */}
      <div className="space-y-3">
        <div className="flex items-center gap-2">
          <Globe className="h-4 w-4 text-warning" />
          <span className="text-sm font-medium text-white">Votre fuseau horaire</span>
        </div>
        <TimezoneSelect
          value={timezone}
          onChange={handleTimezoneChange}
          className="bg-white/[0.02] border-white/10 text-white focus:ring-warning focus:border-warning/50"
        />
      </div>

      {/* Auto planning option */}
      <div className={cn(
        "p-5 rounded-2xl border-2 transition-all",
        isAutoPlanning 
          ? "border-warning/50" 
          : "border-white/10"
      )}>
        <div className="flex items-start justify-between gap-4">
          <div className="flex items-start gap-4">
            <div className="p-3 rounded-xl bg-white/10">
              <Sparkles className={cn(
                "h-6 w-6",
                isAutoPlanning ? "text-warning" : "text-white/60"
              )} />
            </div>
            <div className="space-y-1">
              <h4 className="font-medium text-white">
                Planification intelligente
              </h4>
              <p className="text-sm text-white/50">
                PRSNL optimise votre calendrier éditorial et votre impact
              </p>
            </div>
          </div>
          <Switch checked={isAutoPlanning} onCheckedChange={handleToggle} />
        </div>
      </div>

      {/* Rescheduling strategy - Select dropdown */}
      <div className="space-y-3">
        <p className="text-sm text-white/60">
          Si la publication doit être repoussée, vous préférez :
        </p>
        <Select
          value={reschedulingStrategy}
          onValueChange={(value) => handleStrategyChange(value as ReschedulingStrategy)}
        >
          <SelectTrigger className="w-full h-auto py-8 px-4 bg-white/[0.02] border-white/10 text-white focus:ring-warning focus:border-warning/50 rounded-xl pointer-events-auto">
            {(() => {
              const selected = RESCHEDULING_OPTIONS.find(o => o.value === reschedulingStrategy);
              if (!selected) return <SelectValue placeholder="Choisir une option" />;
              const Icon = selected.icon;
              return (
                <div className="flex items-center gap-3 w-full">
                  <div className="p-2 rounded-lg bg-warning/20">
                    <Icon className="h-4 w-4 text-warning" />
                  </div>
                  <div className="flex-1 min-w-0 text-left">
                    <p className="text-sm font-medium text-white">{selected.label}</p>
                    <p className="text-xs text-white/40 truncate">{selected.description}</p>
                  </div>
                </div>
              );
            })()}
          </SelectTrigger>
          <SelectContent className="bg-card border-white/10 p-1 pointer-events-auto z-[9999]">
            {RESCHEDULING_OPTIONS.map(option => {
              const Icon = option.icon;
              const isSelected = reschedulingStrategy === option.value;
              return (
                <SelectItem
                  key={option.value}
                  value={option.value}
                   className="p-0 focus:bg-transparent data-[highlighted]:bg-transparent [&>span:last-child]:w-full"
                >
                  <div className={cn(
                     "flex items-center gap-3 p-3 rounded-xl transition-all",
                    isSelected
                      ? "bg-warning/10"
                      : "hover:bg-white/[0.05]"
                  )}>
                    <div className={cn(
                      "p-2 rounded-lg",
                      isSelected ? "bg-warning/20" : "bg-white/10"
                    )}>
                      <Icon className={cn(
                        "h-4 w-4",
                        isSelected ? "text-warning" : "text-white/40"
                      )} />
                    </div>
                    <div className="flex-1 min-w-0 text-left">
                      <p className={cn(
                        "text-sm font-medium",
                        isSelected ? "text-white" : "text-white/70"
                      )}>
                        {option.label}
                      </p>
                      <p className="text-xs text-white/40 truncate">
                        {option.description}
                      </p>
                    </div>
                  </div>
                </SelectItem>
              );
            })}
          </SelectContent>
        </Select>
      </div>

      {/* Manual planning config - shown when auto planning is disabled */}
      {!isAutoPlanning && userId && (
        <ManualPlanningConfig userId={userId} compact />
      )}
    </div>
  );
}
